import { useState, useEffect, useRef } from 'react';
import websocketService from '../../services/websocketService';
import { searchUserByPhone, getCurrentUserAccount, getCurrentUserId, searchUserByUid } from '../../services/authService';
import { EVENT_TYPE, ACK_STATUS } from '../../models/eventTypes';
import { createTextMessage, createAckMessage, isValidMessage, getMessageDisplayText } from '../../models/message';
import './ChatPanel.css';

function ChatPanel({ onLogout }) {
  // conversations key is userId (uid), value object { userInfo: {}, messages: [] }
  const [conversations, setConversations] = useState({});
  const [activeUserId, setActiveUserId] = useState(null);
  const [inputText, setInputText] = useState('');
  const [currentUserAccount, setCurrentUserAccount] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  
  const messagesEndRef = useRef(null);
  
  // Refs for accessing latest state in event listeners created in useEffect closure
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;

  // 初始化加载 UserID
  useEffect(() => {
    const account = getCurrentUserAccount();
    if (account) {
        setCurrentUserAccount(account);
    }
    const userId = getCurrentUserId();
    if (userId) {
      setCurrentUserId(userId);
      websocketService.connect();
    } else {
      if (onLogout) onLogout(); 
    }

    const unsubscribeMsg = websocketService.addListener((msg) => {
      console.log('Received message:', msg);

      // 验证消息格式
      if (!isValidMessage(msg)) {
        console.warn('Received invalid message format:', msg);
        return;
      }

      if (msg.event_type === EVENT_TYPE.MESSAGE) {
          handleIncomingMessage(msg);
      } else if (msg.event_type === EVENT_TYPE.ACK) {
          handleIncomingAck(msg);
      }
    });

    const unsubscribeStatus = websocketService.addStatusListener((status) => {
      setConnectionStatus(status);
      if (status === 'auth_error' && onLogout) {
        onLogout();
      }
    });

    return () => {
      unsubscribeMsg();
      unsubscribeStatus();
      websocketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversations, activeUserId]);

  const handleIncomingMessage = async (msg) => {
    // 假设 msg.from_user 是发送者 ID (uid)
    const senderId = msg.from_user;
    const myUid = currentUserIdRef.current;

    // 1. 如果是自己发的消息（多端同步），则添加到 to_user 的会话中
    if (String(senderId) === String(myUid)) {
      const targetId = msg.to_user;
      setConversations(prev => {
        const conversation = prev[targetId] || { userInfo: { name: '未知用户', uid: targetId }, messages: [] };
        const currentList = conversation.messages;
         // 避免重复消息 (使用 msg_id 去重)
        const exists = currentList.some(m => m.msg_id === msg.msg_id);
        if (exists) return prev;

        return {
          ...prev,
          [targetId]: {
             ...conversation,
             messages: [...currentList, msg]
          }
        };
      });
      // 自己发的消息不需要发送ACK
      return;
    }

    // 2. 处理接收到的别人的消息
    // 先检查当前上下文中是否已有该用户的会话信息
    const hasUserInfo = !!conversationsRef.current[senderId];
    let fetchedUserInfo = null;

    if (!hasUserInfo) {
      try {
        console.log(`New user message from ${senderId}, fetching user info...`);
        // 异步获取用户信息
        fetchedUserInfo = await searchUserByUid(senderId);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        // 获取失败，fetchedUserInfo 保持为 null，后续逻辑会处理为"未知用户"
      }
    }

    // 统一执行更新逻辑，将获取到的用户信息（如果有）传进去
    updateConversationWithMsg(senderId, msg, fetchedUserInfo);

    // 3. 发送ACK确认消息给服务器
    sendAckMessage(msg.msg_id);
  };

  /**
   * 发送ACK确认消息
   * @param {string} origMsgId - 原始消息的msg_id
   */
  const sendAckMessage = (origMsgId) => {
    try {
      const ack = createAckMessage({
        origMsgId: origMsgId,
        status: ACK_STATUS.RECEIVED,
      });

      console.log('Sending ACK:', ack);
      websocketService.sendMessage(ack);
    } catch (error) {
      console.error('Failed to send ACK:', error);
    }
  };

  /**
   * 处理接收到的ACK消息
   * 更新对应消息的状态（已送达/已读等）
   */
  const handleIncomingAck = (ackMsg) => {
    if (!ackMsg.ack) return;

    const { orig_msg_id, status } = ackMsg.ack;
    console.log(`Received ACK for message ${orig_msg_id}: ${status}`);

    // 遍历所有会话，找到对应的消息并更新状态
    setConversations(prev => {
      let updated = false;
      const newConversations = { ...prev };

      // 遍历所有会话
      Object.keys(newConversations).forEach(userId => {
        const conversation = newConversations[userId];
        const messageIndex = conversation.messages.findIndex(
          m => m.msg_id === orig_msg_id
        );

        if (messageIndex !== -1) {
          // 找到了对应的消息，更新其ACK状态
          const updatedMessages = [...conversation.messages];
          updatedMessages[messageIndex] = {
            ...updatedMessages[messageIndex],
            ack_status: status, // 添加ACK状态字段
          };

          newConversations[userId] = {
            ...conversation,
            messages: updatedMessages,
          };
          updated = true;
        }
      });

      return updated ? newConversations : prev;
    });
  };

  const updateConversationWithMsg = (userId, msg, fetchedUserInfo) => {
      setConversations(prev => {
        const existingConv = prev[userId];
        const defaultUserInfo = fetchedUserInfo || { name: '未知用户', uid: userId };
        
        let targetUserInfo = existingConv ? existingConv.userInfo : defaultUserInfo;
        let pMessages = existingConv ? existingConv.messages : [];

        // 如果提供了新获取的信息，合并之
        if (fetchedUserInfo) {
            targetUserInfo = { ...targetUserInfo, ...fetchedUserInfo };
        }

        // 避免重复消息 (使用 msg_id 去重)
        const exists = pMessages.some(m => m.msg_id === msg.msg_id);
        if (exists) return prev;
  
        return {
          ...prev,
          [userId]: {
              userInfo: targetUserInfo,
              messages: [...pMessages, msg]
          }
        };
      });
  };

  const handleSendMessage = () => {
    if (!inputText.trim() || !activeUserId || !currentUserId) return;

    // 使用消息工厂函数创建标准格式的消息
    const msgPayload = createTextMessage({
      toUser: activeUserId,
      fromUser: currentUserId,
      content: inputText,
    });

    // 发送 WebSocket 消息
    websocketService.sendMessage(msgPayload);

    // 乐观更新 UI (直接显示在右侧)
    setConversations(prev => {
        const conversation = prev[activeUserId] || { userInfo: { name: '未知用户', uid: activeUserId }, messages: [] };
        return {
          ...prev,
          [activeUserId]: {
              ...conversation,
              messages: [...conversation.messages, msgPayload]
          }
        };
    });

    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };


  const addNewChatUserManual = async () => {
      const phoneNum = prompt("请输入对方的手机号/账号查找:");
      if (!phoneNum) return;

      try {
          const user = await searchUserByPhone(phoneNum);
          console.log('User found:', user);
          
          if (!user || !user.uid) {
             alert('未找到用户');
             return;
          }
          
          // 如果会话已存在，只更新用户信息，不覆盖消息
          setConversations(prev => {
              const existingConversation = prev[user.uid];
              if (existingConversation) {
                  return {
                      ...prev,
                      [user.uid]: {
                          ...existingConversation,
                          userInfo: {
                              ...existingConversation.userInfo,
                              ...user
                          }
                      }
                  };
              }
              
              // 如果是新会话，则初始化
              return {
                  ...prev,
                  [user.uid]: {
                      userInfo: user,
                      messages: []
                  }
              };
          });
          setActiveUserId(user.uid);

      } catch (e) {
          console.error(e);
          alert('查找失败');
      }
  };

  const userList = Object.keys(conversations);

  return (
    <div className="chat-container">
      {/* 左侧用户列表 */}
      <div className="chat-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-title" title={currentUserAccount}>当前: {currentUserAccount ? currentUserAccount : '...'}</div>
          <button className="sidebar-btn-add" onClick={addNewChatUserManual}>+ 新会话</button>
        </div>
        <ul className="user-list">
          {userList.length === 0 && (
            <li className="user-item-empty">暂无消息记录</li>
          )}
          {userList.map(userId => {
            const conversation = conversations[userId];
            const userInfo = conversation.userInfo || {};
            const displayName = userInfo.name || userInfo.phone || userId;

            return (
            <li 
              key={userId}
              className={`user-item ${activeUserId === userId ? 'active' : ''}`}
              onClick={() => setActiveUserId(userId)}
            >
              <div className="user-avatar-placeholder">
                  {displayName.substring(0, 1).toUpperCase()}
              </div>
              <div className="user-info">
                  <div className="user-name" title={userId}>{displayName}</div>
                  <div className="user-last-msg">点击查看消息</div>
              </div>
            </li>
            );
          })}
        </ul>
        <div className="sidebar-footer">
            <button className="btn-logout" onClick={onLogout}>退出登录</button>
        </div>
      </div>

      {/* 右侧聊天区域 */}
      <div className="chat-main">
        {activeUserId && conversations[activeUserId] ? (
          <>
            <div className="chat-header">
                正在与 {conversations[activeUserId].userInfo.name || conversations[activeUserId].userInfo.phone || '未知用户'} 聊天
            </div>
            <div className="chat-messages">
              {(conversations[activeUserId].messages || []).map((msg, index) => {
                const isMyMsg = String(msg.from_user) === String(currentUserId);
                const displayText = getMessageDisplayText(msg);

                // 获取ACK状态显示文本
                let ackStatusText = '';
                if (isMyMsg && msg.ack_status) {
                  switch (msg.ack_status) {
                    case ACK_STATUS.RECEIVED:
                      ackStatusText = '✓';
                      break;
                    case ACK_STATUS.DELIVERED:
                      ackStatusText = '✓✓';
                      break;
                    case ACK_STATUS.READ:
                      ackStatusText = '✓✓';
                      break;
                    case ACK_STATUS.FAILED:
                      ackStatusText = '✗';
                      break;
                    default:
                      ackStatusText = '';
                  }
                }

                return (
                  <div key={index} className={`message ${isMyMsg ? 'sent' : 'received'}`}>
                    <div className="message-content">{displayText}</div>
                    <div className="message-meta">
                      {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      {ackStatusText && <span style={{ marginLeft: '4px' }}>{ackStatusText}</span>}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            
            <div className="chat-input-area">
              <input
                type="text"
                className="chat-input"
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="输入消息..."
              />
              <button 
                className="btn-send" 
                onClick={handleSendMessage}
                disabled={!inputText.trim()}
              >
                发送
              </button>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <div style={{textAlign: 'center'}}>
                <h3>欢迎使用</h3>
                <p>请从左侧列表选择用户开始聊天</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatPanel;
